// src/pages/Examiner/ExamUploadPage.js
import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Card,
  Accordion,
  Table,
  Button,
  Form,
  Alert,
  ProgressBar,
  Badge
} from 'react-bootstrap';
import { FaCheck, FaTimesCircle, FaExclamationTriangle } from 'react-icons/fa';
import TitleWithBack from '../../components/common/TitleWithBack';
import axios from 'axios';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { getUploadPhase, formatCountdown } from '../../utils/examWindow';
import JSZip from 'jszip';
import ScoringGuideTable from './ScoringGuideTable';
import PdfPreviewModal from '../../components/common/PdfPreviewModal';
import ReportEditor from './ReportEditor';
import { useDialog } from '../../context/DialogContext';
import { AuthContext } from '../../context/AuthContext';
import { queueUpload } from '../../utils/offlineQueue';
import { resilientUpload } from '../../utils/resilientUpload';
import { uploadCandidateWorkResumable } from '../../utils/tusUpload';

// Renders as an Accordion.Item (manually expand/collapse, several can stay
// open at once - see the alwaysOpen Accordion below) for the team leader/
// solo examiner, who has enough sections to benefit from being able to
// collapse the ones they're not using. A regular team member only ever sees
// one or two sections anyway, so they keep the plain always-expanded Card
// layout instead.
function Section({ eventKey, title, accordion, children }) {
  if (accordion) {
    return (
      <Accordion.Item eventKey={eventKey} className="mb-3">
        <Accordion.Header>{title}</Accordion.Header>
        <Accordion.Body style={{ backgroundColor: '#f8f9fa', paddingTop: '1.25rem' }}>
          {children}
        </Accordion.Body>
      </Accordion.Item>
    );
  }
  return (
    <Card className="mb-4">
      <Card.Body style={{ backgroundColor: '#f8f9fa' }}>
        <Card.Title style={{ fontWeight: 'bold' }}>{title}</Card.Title>
        <hr />
        {children}
      </Card.Body>
    </Card>
  );
}

export default function ExamUploadPage() {
  const location = useLocation();
  const { id: assignmentId } = useParams();
  const { confirm, prompt } = useDialog();
  const { user } = useContext(AuthContext);
  // location.state only exists after client-side navigation from the Jobs
  // Dashboard - a hard page refresh loses it, which used to leave this page
  // permanently unable to load the candidate roster (nothing downstream of
  // moduleDetails/examCenterId could ever populate). Seed from location.state
  // when present, and fall back to fetching it below once assignmentInfo
  // (which is already refresh-safe) is available.
  const [moduleDetails, setModuleDetails] = useState(location.state?.module || null);
  const [examCenterId, setExamCenterId] = useState(location.state?.examCenterId || null);
  const navigate = useNavigate();
  console.log("Module details:", moduleDetails);
  console.log("Assignment ID:", assignmentId);

  const [requiredDocs, setRequiredDocs] = useState([]);
  const [students, setStudents] = useState([]);
  // State for holding individual candidate files keyed by student reg number.
  const [studentFiles, setStudentFiles] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [candidateWorkUploadStatus, setCandidateWorkUploadStatus] = useState(null);
  const [otherDocsFiles, setOtherDocsFiles] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});
  // Add state for tracking document uploads
  const [docUploadStatus, setDocUploadStatus] = useState({});
  const [progress, setProgress] = useState({});
  const [allCandidateWorkUploaded, setAllCandidateWorkUploaded] = useState(false);
  const [uploadedStudentFiles, setUploadedStudentFiles] = useState({}); // regNo -> fileName
  const [uploadedDocFiles, setUploadedDocFiles] = useState({}); // docName -> fileName
  // Tracks how each entry in studentFiles was populated, so a folder re-scan
  // only ever touches folder-sourced entries and never clobbers a manual pick.
  const [fileSource, setFileSource] = useState({}); // regNo -> 'folder' | 'manual'
  // Per-candidate match status from the last bulk-folder scan - one of
  // 'matched' (green tick) / 'missing' (no folder found, red cross) /
  // 'error' (folder found but couldn't be used, e.g. empty - amber
  // warning) - separate from uploadStatus which only reflects actual
  // upload outcomes.
  const [folderMatchStatus, setFolderMatchStatus] = useState({});
  const [folderErrorReasons, setFolderErrorReasons] = useState({}); // regNo -> reason, only for 'error' entries
  const [folderScanning, setFolderScanning] = useState(false);
  const [folderScanSummary, setFolderScanSummary] = useState(null); // { matchedCount, missingStudents, erroredStudents }
  // Candidates explicitly given a final disposition instead of a file -
  // regNo -> { status: 'ABSENT' | 'EMPTY', reason }. Recorded server-side via
  // /candidate-status so it survives refresh and feeds the accountability report.
  const [finalizedStatus, setFinalizedStatus] = useState({});
  // Folders present in the last scan that didn't match anyone on the roster.
  const [unmatchedFolders, setUnmatchedFolders] = useState([]); // [{ folderName, fileCount }]
  const [lastScanGroups, setLastScanGroups] = useState({}); // raw folder-name -> File[] from the last scan
  const [protestFormOpenFor, setProtestFormOpenFor] = useState(null); // folderName currently being added
  const [protestForm, setProtestForm] = useState({ registrationNumber: '', name: '', email: '' });
  const [protestSubmitting, setProtestSubmitting] = useState(false);

  // Exam upload window: fetched independently of location.state so the
  // countdown/gating still works correctly on a direct page load or refresh.
  const [assignmentInfo, setAssignmentInfo] = useState(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    if (!assignmentId) return;
    // There's no GET /api/exam-assignments/{id} endpoint - fetch the full
    // list (same one JobsDashboard uses) and pick out this assignment, so
    // the countdown/gating works correctly even on a direct page load.
    const fetchAssignmentInfo = () => {
      // Cache-busting param: see the matching comment in JobsDashboard.js -
      // an intermediate proxy ignoring our no-store header would otherwise
      // keep serving the first response to this identical URL forever.
      axios
        .get('/api/exam-assignments', { params: { _: Date.now() } })
        .then((res) => {
          const match = (res.data || []).find((a) => String(a.id) === String(assignmentId));
          setAssignmentInfo(match || null);
        })
        .catch((err) => console.error('Error fetching assignment timing info:', err));
    };
    fetchAssignmentInfo();
    // Poll periodically so a schedule change made by the admin while this
    // page is open (exam date/time) is picked up and the countdown/gating
    // recalculates against it, without needing a manual refresh.
    const interval = setInterval(fetchAssignmentInfo, 30000);
    return () => clearInterval(interval);
  }, [assignmentId]);

  // Refresh-safe fallback: if we landed here without location.state (hard
  // reload, or a bookmarked/shared link), derive moduleDetails/examCenterId
  // from the assignment itself once it loads, instead of leaving the roster
  // permanently unloadable.
  useEffect(() => {
    if (moduleDetails && examCenterId) return;
    if (!assignmentInfo) return;
    if (assignmentInfo.examCentreId) {
      setExamCenterId((prev) => prev || assignmentInfo.examCentreId);
    }
    if (!moduleDetails && assignmentInfo.moduleId) {
      axios
        .get(`/api/modules/${assignmentInfo.moduleId}`)
        .then((res) => setModuleDetails(res.data))
        .catch((err) => console.error('Error fetching module details:', err));
    }
  }, [assignmentInfo, moduleDetails, examCenterId]);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Pre-fill any ABSENT/EMPTY dispositions already recorded for this
  // assignment (e.g. from a previous session) so they survive a refresh.
  useEffect(() => {
    if (!assignmentId || !examCenterId) return;
    axios
      .get('/api/reports/candidate-work-status', { params: { examCentreId: examCenterId } })
      .then((res) => {
        const rows = (res.data || []).filter((r) => String(r.assignmentId) === String(assignmentId));
        const statusMap = {};
        rows.forEach((r) => {
          if (r.status === 'ABSENT' || r.status === 'EMPTY') {
            statusMap[r.registrationNumber] = { status: r.status, reason: r.reason };
          }
        });
        setFinalizedStatus(statusMap);
      })
      .catch((err) => console.error('Error fetching candidate work status:', err));
  }, [assignmentId, examCenterId]);

  const uploadPhase = assignmentInfo ? getUploadPhase(assignmentInfo, now) : { phase: 'unknown', window: null };
  const uploadWindowOpen = uploadPhase.phase === 'during';

  // Load required documents from moduleDetails or fallback.
  useEffect(() => {
    if (moduleDetails && moduleDetails.examType && moduleDetails.examType.requiredDocuments) {
      console.log("Found requiredDocuments in moduleDetails:", moduleDetails.examType.requiredDocuments);
      setRequiredDocs(moduleDetails.examType.requiredDocuments);
    } else {
      console.log("Module details missing or requiredDocuments not found. Fetching fallback...");
      axios
        .get('/api/required-docs')
        .then((res) => {
          setRequiredDocs(res.data || []);
        })
        .catch((err) => {
          console.error('Error fetching required docs:', err);
        });
    }
  }, [moduleDetails]);

  console.log("requiredDocs:", requiredDocs);

  // Load students.
  useEffect(() => {
    if (!moduleDetails || !examCenterId) return;
  
    const params = {
      programId: moduleDetails.program?.id,
      yearOfStudyId: moduleDetails.yearOfStudy?.id,
      semesterId: moduleDetails.semester?.id,
      examCenterId: examCenterId,
      status: 'active',
      moduleId: moduleDetails.id
    };
    const query = Object.entries(params)
      .filter(([k, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
  
    axios
      .get(`/api/students/filter?${query}`)
      .then((res) => {
        setStudents(res.data || []);
      })
      .catch((err) => {
        console.error('Error fetching student list:', err);
      });
  }, [moduleDetails, examCenterId]);

 // Matches "Candidates work" or "Candidates' work" (straight or curly
  // apostrophe) - tolerant of the punctuation drift between CAD's and OSM's
  // seeded document names, both of which have existed in real (including
  // production) data. See ExamTypeDataLoader on the backend for the
  // matching startup reconciliation that removes the stale OSM/RLP entry at
  // the source; this check is a second, defense-in-depth guard so the UI is
  // correct even against a database that hasn't run that reconciliation yet.
  const isCandidatesWorkDoc = (doc) => /^candidates['’]?\s*work$/i.test((doc || '').trim());

  // RLP/OSM modules get an interactive scoring table instead of a plain file
  // upload for "Scoring guide". Both "Scoring guide" and "Mark sheet" are
  // auto-generated server-side (as real PDFs, from the same data the table
  // captures) the moment every candidate is fully scored - see
  // ScoringGuideExportService.attachAutoGeneratedPdfsIfComplete on the
  // backend - so neither ever gets a manual upload slot here for these
  // module types. docUploadStatus for both comes purely from the real
  // upload-status poll below, not from any client-side "looks complete" guess.
  const isScoringGuideModule = moduleDetails?.examType?.name === 'RLP' || moduleDetails?.examType?.name === 'OSM';

  // OSM/RLP are scored on-system and must never surface a per-candidate file
  // upload field, regardless of what a stale required-documents list says.
  const showCandidatesWork = !isScoringGuideModule && requiredDocs.some(isCandidatesWorkDoc);
  const otherRequiredDocs = requiredDocs.filter((doc) => !isCandidatesWorkDoc(doc));
  const showScoringGuideTable = isScoringGuideModule && requiredDocs.includes('Scoring guide');
  const showAutoGeneratedMarksheetStatus = isScoringGuideModule && requiredDocs.includes('Mark sheet');
  // Every exam type's "Report" gets the typed/formatted-in-browser editor
  // instead of a plain file picker.
  const showReportEditor = requiredDocs.includes('Report');
  const plainOtherRequiredDocs = otherRequiredDocs.filter((doc) =>
    !(doc === 'Scoring guide' && isScoringGuideModule)
    && !(doc === 'Mark sheet' && isScoringGuideModule)
    && doc !== 'Report'
  );
  const [scoringGuideComplete, setScoringGuideComplete] = useState(false);
  const [docPreviewKind, setDocPreviewKind] = useState(null); // 'marksheet' | 'guide' | null

  // Handler for individual candidate file selection.
  const handleStudentFileChange = (regNo, file) => {
    setStudentFiles((prev) => ({
      ...prev,
      [regNo]: file
    }));
    setFileSource((prev) => ({ ...prev, [regNo]: 'manual' }));
  };

  // Helper: check if all students are accounted for - either a file staged/
  // already uploaded, or a final ABSENT/EMPTY disposition recorded.
  const allStudentsReadyForBulk = students.length > 0 && students.every(student => {
    const regNo = student.registrationNumber;
    return studentFiles[regNo] || uploadedStudentFiles[regNo] || finalizedStatus[regNo];
  });

  // A folder name only reliably identifies a candidate via their (unique,
  // exact) registration number - names alone are too fragile to match on
  // (spacing/casing/order vary). Strip everything but letters/digits so
  // "UVT660/U/26/M/0002" and "UVT660-U-26-M-0002" compare equal.
  const normalizeForMatch = (s) => (s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

  const ARCHIVE_EXTENSIONS = ['zip', 'rar', '7z'];
  const getFileExtension = (fileName) => (fileName || '').split('.').pop().toLowerCase();
  const isArchiveFile = (fileName) => ARCHIVE_EXTENSIONS.includes(getFileExtension(fileName));

  // Groups the flat FileList from a <input webkitdirectory> selection into
  // one group per candidate. A candidate can be represented either by a
  // subfolder full of loose files (needs zipping), or by a single archive
  // file sitting directly in the root - e.g. "UVT660-...-001.zip" - which a
  // candidate already zipped themselves and should be used as-is.
  const groupFilesByCandidateFolder = (fileList) => {
    const groups = {};
    Array.from(fileList).forEach((file) => {
      const relPath = file.webkitRelativePath || file.name;
      const parts = relPath.split('/');
      if (parts.length === 2) {
        // A file sitting directly in the root - its own name is its group.
        groups[parts[1]] = [file];
      } else if (parts.length > 2) {
        const candidateFolderName = parts[1];
        if (!groups[candidateFolderName]) groups[candidateFolderName] = [];
        groups[candidateFolderName].push(file);
      }
    });
    return groups;
  };

  // Builds the File to upload for one candidate from their matched folder -
  // shared by the main scan loop and the "add candidate under protest" flow
  // so both produce identical output. Throws if the folder is empty.
  const buildCandidateFileFromFolder = async (student, candidateFiles) => {
    const regNo = student.registrationNumber;
    const moduleName = moduleDetails?.moduleName || 'Module';
    const moduleCode = moduleDetails?.moduleCode || 'Code';

    if (!candidateFiles || candidateFiles.length === 0 || candidateFiles.every((f) => f.size === 0)) {
      throw new Error('Folder was found but is empty (no files inside it).');
    }

    if (candidateFiles.length === 1 && isArchiveFile(candidateFiles[0].name)) {
      // Already zipped by the candidate - use it as-is. Re-zipping an
      // archive just wraps it in a second, useless layer.
      const original = candidateFiles[0];
      const ext = getFileExtension(original.name);
      const fileName = `${student.name}_${regNo}_${moduleName}_${moduleCode}.${ext}`.replace(/\s+/g, '_');
      return new File([original], fileName, { type: original.type || 'application/zip' });
    }

    const zip = new JSZip();
    candidateFiles.forEach((file) => {
      const relParts = (file.webkitRelativePath || file.name).split('/');
      const innerPath = relParts.slice(2).join('/') || file.name;
      zip.file(innerPath, file);
    });
    // STORE (no compression) instead of the JSZip default DEFLATE - CAD
    // deliverables (drawings, renders) are large binary files that are
    // already effectively incompressible, and running DEFLATE over them
    // was slow/memory-heavy enough in the browser to throw and abort the
    // scan silently. STORE is fast and just as space-efficient here.
    const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'STORE' });
    const zipFileName = `${student.name}_${regNo}_${moduleName}_${moduleCode}.zip`.replace(/\s+/g, '_');
    return new File([zipBlob], zipFileName, { type: 'application/zip' });
  };

  // A confirmed-empty folder still gets uploaded, as evidence that it was
  // checked and genuinely contains nothing - a browser can't distinguish "no
  // folder at all" from "a folder with zero files" (directories without any
  // files never appear in a webkitdirectory scan), so this same placeholder
  // covers every confirmed-empty case: a matched folder that turned out
  // empty, a roster candidate with no folder the examiner knows is actually
  // an empty one, and a not-on-roster candidate added under protest with an
  // empty folder.
  const buildEmptyPlaceholderZip = async (student) => {
    const regNo = student.registrationNumber;
    const moduleName = moduleDetails?.moduleName || 'Module';
    const moduleCode = moduleDetails?.moduleCode || 'Code';
    const zip = new JSZip();
    const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'STORE' });
    const zipFileName = `${student.name}_${regNo}_${moduleName}_${moduleCode}_EMPTY.zip`.replace(/\s+/g, '_');
    return new File([zipBlob], zipFileName, { type: 'application/zip' });
  };

  // Bulk folder upload: examiner selects one parent folder containing one
  // subfolder per candidate (named with the candidate's name and
  // registration number). We match each candidate against the roster by
  // registration number, compress each matched candidate's folder into a
  // single zip client-side (reusing the exact zip-per-student shape the
  // existing upload flow already expects), and report per-candidate
  // match status before anything is saved.
  const handleFolderSelect = async (e) => {
    const input = e.target;
    // For large folder trees (CAD candidate folders can be hundreds of
    // files) some Chromium builds fire `change` slightly before the
    // directory's FileList has finished being populated, so input.files
    // reads back empty on the first check even though a real folder was
    // selected - that's what caused this to fail intermittently rather
    // than consistently. Re-check a few times with a short backoff before
    // concluding nothing was actually selected.
    let fileList = input.files;
    for (let attempt = 0; (!fileList || fileList.length === 0) && attempt < 4; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 100 * (attempt + 1)));
      fileList = input.files;
    }
    if (!fileList || fileList.length === 0) {
      setFeedback({ type: 'danger', message: 'No folder was selected, or the selected folder is empty.' });
      return;
    }
    if (students.length === 0) {
      setFeedback({ type: 'danger', message: 'Candidate list has not loaded yet - please wait and try again.' });
      input.value = null;
      return;
    }

    setFolderScanning(true);
    setFeedback(null);
    try {
      const groups = groupFilesByCandidateFolder(fileList);
      const candidateFolderNames = Object.keys(groups);

      const newFolderMatchStatus = {};
      const newFolderErrorReasons = {};
      const newStudentFiles = {};
      const newFileSource = {};
      const missingStudents = [];
      const erroredStudents = [];
      const matchedFolderNames = new Set();
      let matchedCount = 0;

      for (const student of students) {
        const regNo = student.registrationNumber;
        // A candidate already given a final disposition (absent/empty) in an
        // earlier scan or a previous session doesn't need to be re-matched.
        if (finalizedStatus[regNo]) continue;
        const normalizedReg = normalizeForMatch(regNo);
        const matchedFolderName = candidateFolderNames.find(
          (name) => normalizeForMatch(name).includes(normalizedReg)
        );

        if (!matchedFolderName) {
          newFolderMatchStatus[regNo] = 'missing';
          missingStudents.push(student);
          continue;
        }
        matchedFolderNames.add(matchedFolderName);

        // A candidate's folder is processed in isolation: if it turns out to
        // be empty (or anything else about it fails - a corrupt archive, a
        // JSZip error) that only marks THIS candidate as needing attention
        // instead of aborting the scan for every other candidate already
        // processed successfully.
        try {
          newStudentFiles[regNo] = await buildCandidateFileFromFolder(student, groups[matchedFolderName]);
          newFileSource[regNo] = 'folder';
          newFolderMatchStatus[regNo] = 'matched';
          matchedCount += 1;
        } catch (err) {
          console.error(`Error processing folder for candidate ${regNo}:`, err);
          newFolderMatchStatus[regNo] = 'error';
          newFolderErrorReasons[regNo] = err.message || 'Could not process this folder.';
          erroredStudents.push(student);
        }
      }

      // Folders that don't match anyone on the roster at all - may be a
      // candidate who sat under protest and needs to be added first.
      const unmatched = candidateFolderNames
        .filter((name) => !matchedFolderNames.has(name))
        .map((name) => ({ folderName: name, fileCount: groups[name].length }));
      setUnmatchedFolders(unmatched);
      setLastScanGroups(groups);

      setStudentFiles((prev) => {
        const next = { ...prev };
        // Drop stale folder-sourced entries from a previous scan that no
        // longer have a match in this one - never touches manual picks.
        Object.keys(next).forEach((regNo) => {
          if (fileSource[regNo] === 'folder' && !(regNo in newStudentFiles)) {
            delete next[regNo];
          }
        });
        return { ...next, ...newStudentFiles };
      });
      setFileSource((prev) => ({ ...prev, ...newFileSource }));
      setFolderMatchStatus(newFolderMatchStatus);
      setFolderErrorReasons(newFolderErrorReasons);
      setFolderScanSummary({ matchedCount, missingStudents, erroredStudents });

      if (missingStudents.length === 0 && erroredStudents.length === 0 && unmatched.length === 0) {
        setFeedback({ type: 'success', message: `All ${students.length} candidate folders matched. Review below, then click "Upload All Candidate Work" to save.` });
      } else {
        const parts = [];
        if (missingStudents.length > 0) parts.push(`${missingStudents.length} folder(s) missing or unmatched - mark absent or re-select the corrected folder`);
        if (erroredStudents.length > 0) parts.push(`${erroredStudents.length} folder(s) found but need attention (see warning icons below)`);
        if (unmatched.length > 0) parts.push(`${unmatched.length} folder(s) don't match anyone on the roster (see below - may need adding under protest)`);
        setFeedback({ type: 'danger', message: `${matchedCount} of ${students.length} candidate folders matched. ${parts.join('; ')}.` });
      }
    } catch (err) {
      // Previously this had no catch, so any failure here (e.g. running out
      // of memory zipping large CAD files) silently aborted the scan with no
      // feedback at all - the page just appeared to do nothing.
      console.error('Error scanning candidate folders:', err);
      setFeedback({ type: 'danger', message: `Error scanning the selected folder: ${err.message || 'unknown error'}. If the folder contains very large files, try selecting fewer candidates at a time.` });
    } finally {
      setFolderScanning(false);
      input.value = null; // allow re-selecting the same folder on a redo
    }
  };

  // "No folder at all" (ABSENT) has nothing to upload - recorded immediately.
  const handleMarkAbsent = async (student) => {
    const regNo = student.registrationNumber;
    const reason = await prompt(
      `Confirm that ${student.name} (${regNo}) has no folder and should be marked ABSENT. Provide a reason/comment for accountability:`,
      { title: 'Mark Candidate Absent', confirmText: 'Mark Absent', variant: 'danger', placeholder: 'e.g. Candidate did not attend the exam session' }
    );
    if (reason === null) return; // cancelled
    try {
      await axios.post(`/api/assignments/${assignmentId}/candidate-status`, [
        { registrationNumber: regNo, status: 'ABSENT', reason }
      ]);
      setFinalizedStatus((prev) => ({ ...prev, [regNo]: { status: 'ABSENT', reason } }));
      // An absence replaces any staged file/match state for this candidate.
      setStudentFiles((prev) => {
        const next = { ...prev };
        delete next[regNo];
        return next;
      });
      setFeedback({ type: 'success', message: `${student.name} (${regNo}) recorded as absent.` });
    } catch (err) {
      console.error(`Error recording absent status for ${regNo}:`, err);
      const backendMessage = err.response?.data?.error || (typeof err.response?.data === 'string' ? err.response.data : null);
      setFeedback({ type: 'danger', message: backendMessage || `Failed to record absent status for ${regNo}.` });
    }
  };

  // A confirmed-empty folder is still uploaded (as the empty placeholder) -
  // stage it like any other candidate's file so it goes through the normal
  // "Upload All Candidate Work" flow, and remember the reason locally so it
  // can be (re-)recorded as EMPTY immediately after that upload succeeds -
  // otherwise the upload's own auto-recording would silently overwrite this
  // with a plain SUBMITTED status and lose the accountability reason.
  const handleConfirmEmpty = async (student) => {
    const regNo = student.registrationNumber;
    const reason = await prompt(
      `Confirm that ${student.name} (${regNo}) has a folder but it is empty (no files). The empty folder will still be uploaded as their submission. Provide a reason/comment for accountability:`,
      { title: 'Confirm Empty Folder', confirmText: 'Confirm Empty & Upload', variant: 'warning', placeholder: 'e.g. Candidate attended but submitted no work' }
    );
    if (reason === null) return; // cancelled
    try {
      const file = await buildEmptyPlaceholderZip(student);
      setStudentFiles((prev) => ({ ...prev, [regNo]: file }));
      setFileSource((prev) => ({ ...prev, [regNo]: 'folder' }));
      setFolderMatchStatus((prev) => ({ ...prev, [regNo]: 'matched' }));
      setFinalizedStatus((prev) => ({ ...prev, [regNo]: { status: 'EMPTY', reason } }));
      setFeedback({ type: 'success', message: `${student.name} (${regNo})'s empty folder confirmed and staged. Click "Upload All Candidate Work" to save.` });
    } catch (err) {
      console.error(`Error preparing empty folder for ${regNo}:`, err);
      setFeedback({ type: 'danger', message: `Failed to prepare the empty folder for ${regNo}.` });
    }
  };

  // Sentinel used in place of a real folder name when the examiner is adding
  // a not-on-roster candidate who has no folder / an empty one - there's
  // nothing to scan for that case (a browser can't detect an empty
  // directory at all), so it's a manual entry point rather than tied to a
  // row in the unmatched-folders list.
  const MANUAL_EMPTY_PROTEST = '__MANUAL_EMPTY__';

  // A folder exists but doesn't match anyone on the roster - add the
  // candidate on the spot (flagged as having sat under protest) and
  // immediately stage their folder for upload, same as any other candidate.
  // When folderName is the manual-empty sentinel, there's no scanned folder
  // at all - the examiner is asserting the candidate exists with an empty
  // folder, so the same confirmed-empty placeholder/reason flow applies.
  const handleAddProtestCandidate = async (folderName) => {
    const isManualEmpty = folderName === MANUAL_EMPTY_PROTEST;
    const { registrationNumber, name, email } = protestForm;
    if (!registrationNumber.trim() || !name.trim() || !email.trim()) {
      setFeedback({ type: 'danger', message: 'Registration number, name, and email are all required to add a candidate under protest.' });
      return;
    }
    if (!moduleDetails?.program?.id || !examCenterId) {
      setFeedback({ type: 'danger', message: 'Missing program/exam centre context - cannot add candidate.' });
      return;
    }

    let reason = null;
    if (isManualEmpty) {
      reason = await prompt(
        `Confirm that ${name.trim()} (${registrationNumber.trim()}) is not on the roster, sat under protest, and has an empty folder (no files). It will still be recorded and uploaded as their submission. Provide a reason/comment for accountability:`,
        { title: 'Add Under Protest - Empty Folder', confirmText: 'Confirm & Add', variant: 'warning', placeholder: 'e.g. Candidate not on roster, attended but submitted no work' }
      );
      if (reason === null) return; // cancelled
    }

    // Every other registration number in this system uses "/" as the
    // segment separator (e.g. "UVT660/U/26/M/NDICT/0016") - but examiners
    // naturally type what they see on the folder name, which is usually
    // hyphenated. A hyphen-only value here would otherwise be saved verbatim
    // and become unsearchable/inconsistent with the rest of the roster, so
    // normalize it before submitting (only when the examiner hasn't already
    // used slashes themselves).
    const rawRegNo = registrationNumber.trim();
    const normalizedRegNo = (!rawRegNo.includes('/') && rawRegNo.includes('-'))
      ? rawRegNo.replace(/-/g, '/')
      : rawRegNo;

    setProtestSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim(),
        registrationNumber: normalizedRegNo,
        program: { id: moduleDetails.program.id },
        examCenter: { id: examCenterId },
        yearOfStudy: moduleDetails.yearOfStudy ? { id: moduleDetails.yearOfStudy.id } : null,
        semester: moduleDetails.semester ? { id: moduleDetails.semester.id } : null,
        status: 'active',
        underProtest: true
      };
      const res = await axios.post('/api/students', payload);
      const newStudent = res.data;

      const file = isManualEmpty
        ? await buildEmptyPlaceholderZip(newStudent)
        : await buildCandidateFileFromFolder(newStudent, lastScanGroups[folderName] || []);

      setStudents((prev) => [...prev, newStudent]);
      setStudentFiles((prev) => ({ ...prev, [newStudent.registrationNumber]: file }));
      setFileSource((prev) => ({ ...prev, [newStudent.registrationNumber]: 'folder' }));
      setFolderMatchStatus((prev) => ({ ...prev, [newStudent.registrationNumber]: 'matched' }));
      if (isManualEmpty) {
        setFinalizedStatus((prev) => ({ ...prev, [newStudent.registrationNumber]: { status: 'EMPTY', reason } }));
      } else {
        setUnmatchedFolders((prev) => prev.filter((f) => f.folderName !== folderName));
      }
      setProtestFormOpenFor(null);
      setProtestForm({ registrationNumber: '', name: '', email: '' });
      const formatNote = normalizedRegNo !== rawRegNo ? ` (saved as ${normalizedRegNo} to match the system's registration number format)` : '';
      const emptyNote = isManualEmpty ? ' with an empty folder' : '';
      setFeedback({ type: 'success', message: `${name} added to the roster (sat under protest)${emptyNote}${formatNote} and staged. Click "Upload All Candidate Work" once everyone is accounted for.` });
    } catch (err) {
      console.error('Error adding protest candidate:', err);
      const backendMessage = err.response?.data?.error || (typeof err.response?.data === 'string' ? err.response.data : null);
      setFeedback({ type: 'danger', message: backendMessage || 'Failed to add candidate under protest. The registration number or email may already be in use.' });
    } finally {
      setProtestSubmitting(false);
    }
  };

  // Compress all individual candidate files into one ZIP and upload.
  // CAD Phase 2: each candidate's file is now its own resumable tus upload
  // (uploadCandidateWorkResumable) rather than one all-or-nothing multipart
  // POST of every file at once. A small worker pool runs a bounded number
  // concurrently; aggregate progress is bytes-confirmed-received across all
  // of them, not just bytes-sent for one big request. This is the actual
  // point of Phase 2 - a single candidate's transfer dying partway through
  // a large batch no longer means restarting the whole batch, and
  // tus-js-client can resume that one file's transfer after a page reload
  // instead of starting it over from byte zero.
  const BULK_UPLOAD_CONCURRENCY = 3;

  const handleUploadAllCandidateWork = async () => {
    if (!uploadWindowOpen) {
      setFeedback({ type: 'danger', message: 'Uploads are only allowed during the exam upload window.' });
      return;
    }
    // Only an ABSENT candidate has nothing to send; a confirmed-EMPTY
    // candidate has a placeholder file staged just like any real submission.
    const regNos = [];
    const files = [];
    const alreadyUploaded = [];
    const studentsNeedingAction = students.filter((student) => finalizedStatus[student.registrationNumber]?.status !== 'ABSENT');
    if (studentsNeedingAction.length === 0) {
      // Every candidate was marked absent - nothing left to upload.
      setCandidateWorkUploadStatus('success');
      setAllCandidateWorkUploaded(true);
      setFeedback({ type: 'success', message: 'All candidates are accounted for (absent) - nothing further to upload.' });
      return;
    }
    studentsNeedingAction.forEach(student => {
      const regNo = student.registrationNumber;
      if (studentFiles[regNo]) {
        regNos.push(regNo);
        files.push(studentFiles[regNo]);
      } else if (uploadedStudentFiles[regNo]) {
        alreadyUploaded.push(regNo);
      }
    });
    if (regNos.length + alreadyUploaded.length !== studentsNeedingAction.length) {
      setFeedback({ type: 'danger', message: 'Every candidate must have a file selected, already be uploaded, or be marked absent/empty.' });
      return;
    }
    const absentCount = students.length - studentsNeedingAction.length;
    const proceed = await confirm(
      `You are about to upload work for ${regNos.length + alreadyUploaded.length} candidate(s)` +
        (absentCount > 0 ? ` (${absentCount} others already recorded as absent).` : '.') +
        ' Once uploaded, individual files cannot be replaced. Continue?',
      { title: 'Upload All Candidate Work', confirmText: 'Upload', variant: 'primary' }
    );
    if (!proceed) return;

    const token = localStorage.getItem('token');
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    const uploadedBytesByRegNo = {};
    const updateAggregateProgress = () => {
      const uploaded = Object.values(uploadedBytesByRegNo).reduce((sum, bytes) => sum + bytes, 0);
      const percent = totalBytes > 0 ? Math.round((uploaded / totalBytes) * 100) : 100;
      setProgress((prev) => ({ ...prev, allCandidates: percent }));
    };

    const succeededRegNos = [];
    const failedRegNos = [];
    const queue = regNos.map((regNo, idx) => ({ regNo, file: files[idx] }));

    async function worker() {
      while (queue.length > 0) {
        const { regNo, file } = queue.shift();
        try {
          await uploadCandidateWorkResumable(file, {
            assignmentId,
            regNo,
            fileName: file.name,
            token,
            onProgress: (fraction) => {
              uploadedBytesByRegNo[regNo] = fraction * file.size;
              updateAggregateProgress();
            },
          });
          uploadedBytesByRegNo[regNo] = file.size;
          updateAggregateProgress();
          succeededRegNos.push(regNo);
        } catch (err) {
          console.error(`Error uploading candidate work for ${regNo}:`, err);
          failedRegNos.push(regNo);
        }
      }
    }
    await Promise.all(
      Array.from({ length: Math.min(BULK_UPLOAD_CONCURRENCY, queue.length) }, worker)
    );

    if (failedRegNos.length === 0) {
      // The upload itself auto-records every uploaded candidate as SUBMITTED
      // server-side, which would silently overwrite the EMPTY disposition
      // (and its accountability reason) for anyone confirmed empty in this
      // batch - re-assert EMPTY for those specific candidates now that their
      // placeholder file has actually been saved, so it has the last word.
      const emptyRegNosInBatch = regNos.filter((regNo) => finalizedStatus[regNo]?.status === 'EMPTY');
      if (emptyRegNosInBatch.length > 0) {
        try {
          await axios.post(`/api/assignments/${assignmentId}/candidate-status`,
            emptyRegNosInBatch.map((regNo) => ({
              registrationNumber: regNo,
              status: 'EMPTY',
              reason: finalizedStatus[regNo].reason
            }))
          );
        } catch (sealErr) {
          console.error('Error re-asserting empty status after upload:', sealErr);
        }
      }
      setFeedback({ type: 'success', message: 'Candidate work uploaded successfully as a compressed file.' });
      setCandidateWorkUploadStatus('success');
      setAllCandidateWorkUploaded(true);
      // Clear individual file selections after successful bulk upload
      setStudentFiles({});
      setFileSource({});
      setFolderMatchStatus({});
      setFolderErrorReasons({});
      setFolderScanSummary(null);
      setProgress((prev) => ({ ...prev, allCandidates: 0 }));
    } else if (succeededRegNos.length === 0) {
      // Nothing got through at all - treat the same way a fully offline
      // bulk upload always has: queue it locally for ConnectivityContext's
      // sync manager to replay later. (Individual failed uploads may
      // already be resumable via tus's own fingerprint storage too - this
      // is the fallback for when even that couldn't make progress.)
      await queueUpload(assignmentId, {
        regNos,
        files: files.map((file) => ({ blob: file, fileName: file.name })),
        alreadyUploaded
      });
      setFeedback({ type: 'warning', message: "Upload could not complete - this batch has been saved on this device and will upload automatically once you're back online." });
      setStudentFiles({});
      setFileSource({});
      setFolderMatchStatus({});
      setFolderErrorReasons({});
      setFolderScanSummary(null);
      setProgress((prev) => ({ ...prev, allCandidates: 0 }));
    } else {
      // Partial success: keep the failed candidates' file selections so
      // re-clicking Upload only re-sends those, not the whole batch again.
      setUploadedStudentFiles((prev) => {
        const next = { ...prev };
        succeededRegNos.forEach((regNo) => {
          const file = files[regNos.indexOf(regNo)];
          next[regNo] = file.name;
        });
        return next;
      });
      setStudentFiles((prev) => {
        const next = { ...prev };
        succeededRegNos.forEach((regNo) => delete next[regNo]);
        return next;
      });
      setFeedback({
        type: 'warning',
        message: `${succeededRegNos.length} of ${regNos.length} candidate file(s) uploaded. `
          + `${failedRegNos.length} failed and can be retried by clicking Upload again.`,
      });
      setProgress((prev) => ({ ...prev, allCandidates: 0 }));
    }
  };

   // Single file upload handler. Same risk class as the bulk path (one
   // candidate's large CAD file on an unreliable connection) so it gets the
   // same tus resumable transport - see uploadCandidateWorkResumable and the
   // design note above handleUploadAllCandidateWork. Small required
   // documents (handleUploadDocument, below) deliberately stay on
   // resilientUpload/multipart instead: they're a few hundred KB at most,
   // where tus's extra create+patch round trip and fingerprint bookkeeping
   // buy nothing.
const handleUploadSingleCandidateWork = async (regNo) => {
  if (!uploadWindowOpen) {
    setFeedback({ type: 'danger', message: 'Uploads are only allowed during the exam upload window.' });
    return;
  }
  const alreadyUploaded = uploadStatus[regNo] === 'success';
  if (alreadyUploaded) {
    setFeedback({ type: 'danger', message: `File already uploaded for student ${regNo}. Replacement is not allowed.` });
    return;
  }
  const file = studentFiles[regNo];
  if (!file) {
    setFeedback({ type: 'danger', message: `No file selected for student ${regNo}.` });
    return;
  }
  try {
    const student = students.find(s => s.registrationNumber === regNo);
    const moduleName = moduleDetails?.moduleName || 'Module';
    const moduleCode = moduleDetails?.moduleCode || 'Code';
    const fileName = `${student.name}_${regNo}_${moduleName}_${moduleCode}.zip`.replace(/\s+/g, '_');
    const renamedFile = new File([file], fileName, { type: file.type });

    const token = localStorage.getItem('token');
    await uploadCandidateWorkResumable(renamedFile, {
      assignmentId,
      regNo,
      fileName,
      token,
      onProgress: (fraction) => {
        setProgress((prev) => ({ ...prev, [regNo]: Math.round(fraction * 100) }));
      },
    });
    setFeedback({
      type: 'success',
      message: `Candidate work for student ${regNo} uploaded successfully.`
    });
    setUploadStatus((prev) => ({
      ...prev,
      [regNo]: 'success',
    }));
    setAllCandidateWorkUploaded(false); // If uploading individually, bulk upload is not considered done
  } catch (err) {
    console.error('Error uploading candidate work:', err);
    setFeedback({ 
      type: 'danger', 
      message: `Failed to upload candidate work for student ${regNo}.` 
    });
    setUploadStatus((prev) => ({
      ...prev,
      [regNo]: 'error',
    }));
    setProgress((prev) => ({ ...prev, [regNo]: 0 }));
  }
};

  // Handler for other document file changes.
  const handleOtherDocFileChange = (docName, file) => {
    setOtherDocsFiles((prev) => ({
      ...prev,
      [docName]: file
    }));
  };

  // Upload for required documents (non-candidate work).
  const handleUploadDocument = (docName) => {
    if (!uploadWindowOpen) {
      setFeedback({ type: 'danger', message: 'Uploads are only allowed during the exam upload window.' });
      return;
    }
    const alreadyUploaded = docUploadStatus[docName];
    if (alreadyUploaded) {
      setFeedback({ type: 'danger', message: `File already uploaded for ${docName}. Replacement is not allowed.` });
      return;
    }
    const file = otherDocsFiles[docName];
    if (!file) {
      setFeedback({ type: 'danger', message: `No file selected for ${docName}.` });
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', docName);
    formData.append('compressed', 'false');
    resilientUpload(`/api/assignments/${assignmentId}/documents`, formData, {
        onProgress: (event) => {
          const percent = Math.round((event.loaded * 100) / event.total);
          setProgress((prev) => ({ ...prev, [docName]: percent }));
        },
        onRetry: (attempt, maxAttempts) => {
          setFeedback({
            type: 'warning',
            message: `Upload interrupted for ${docName} - retrying (${attempt}/${maxAttempts - 1})...`,
          });
        },
      })
      .then(() => {
        setFeedback({ type: 'success', message: `${docName} uploaded successfully.` });
        setDocUploadStatus((prev) => ({ ...prev, [docName]: true })); 
        setProgress((prev) => ({ ...prev, [docName]: 0 }));
      })
      .catch((err) => {
        console.error('Document upload error:', err);
        setFeedback({ type: 'danger', message: `Failed to upload ${docName}.` });
        setProgress((prev) => ({ ...prev, [docName]: 0 }));
      });
  };

  // Compute if all required docs are uploaded
  const allOtherDocsUploaded = otherRequiredDocs.every(doc => docUploadStatus[doc]);
  const allStudentsUploaded = students.length > 0 && students.every(student => {
    const regNo = student.registrationNumber;
    return uploadStatus[regNo] === 'success' || !!finalizedStatus[regNo];
  });
  const canMarkComplete =
    allOtherDocsUploaded &&
    (
      (!showCandidatesWork) ||
      allCandidateWorkUploaded ||
      allStudentsUploaded
    );
  // Only the team leader may mark a multi-examiner job complete, submit the
  // Assessment Report, or upload the other required documents (Attendance
  // List, etc.) - the backend enforces this as the real boundary for all
  // three, and a non-leader team member sees nothing at all for these
  // sections (not even a placeholder message). Team members still
  // score/upload their own allocated candidates' work freely - unaffected.
  const isTeamAssignment = (assignmentInfo?.examiners?.length || 0) > 1;
  const canActAsTeamLeader = !isTeamAssignment || assignmentInfo?.teamLeader?.id === user?.id;
  // Add: check if all students have selected a file
  const allStudentsSelectedFile = students.length > 0 && students.every(student => !!studentFiles[student.registrationNumber]);

  // Handler for marking upload as complete.
  const handleMarkUploadingComplete = () => {
    if (!uploadWindowOpen) {
      setFeedback({ type: 'danger', message: 'Uploads are only allowed during the exam upload window.' });
      return;
    }
    setFeedback({ type: 'info', message: 'Marking the job as done…' });

    axios
      .post(`/api/assignments/${assignmentId}/exam-centers/${examCenterId}/mark-done`)
      .then(() => {
        setFeedback({ 
          type: 'success', 
          message: 'Job successfully marked as done!' 
        });
        setTimeout(() => {
          navigate('/examiner/jobs');
        }, 1000);
      })
      .catch((err) => {
        console.error('Mark uploading complete error:', err);
        // The server says exactly what's blocking completion (e.g. how many
        // candidates still need work uploaded, or that only the team leader
        // can do this) - show that rather than a guess.
        const backendMessage = err.response?.data?.error || (typeof err.response?.data === 'string' ? err.response.data : null);
        setFeedback({
          type: 'danger',
          message: backendMessage || 'Failed to mark uploading as complete. Please ensure all required documents are uploaded and try again.'
        });
      });
  };

  // Recovery for a stuck "Pending" Scoring guide/Mark sheet: the automatic
  // generation only ever runs as a side effect of a *new* score save and
  // silently swallows failures server-side (see
  // ScoringGuideExportService.attachAutoGeneratedPdfsIfComplete), so a
  // transient failure there - a LibreOffice hiccup, a timeout - otherwise
  // leaves the team permanently stuck with no way to retry and no
  // explanation. Only shown once scoring genuinely looks complete
  // (scoringGuideComplete), so this isn't offered as a way to skip ahead.
  const [regeneratingDocs, setRegeneratingDocs] = useState(false);
  const handleRegenerateAutoDocuments = () => {
    setRegeneratingDocs(true);
    setFeedback(null);
    axios
      .post(`/api/scoring-criteria/assignment/${assignmentId}/regenerate-auto-documents`)
      .then(() => {
        setFeedback({ type: 'success', message: 'Scoring guide and Mark sheet regenerated and attached.' });
        return axios.get(`/api/assignments/${assignmentId}/upload-status`);
      })
      .then((res) => {
        const { requiredDocUploads } = res.data || {};
        if (requiredDocUploads) {
          const newDocUploadStatus = {};
          const newUploadedDocFiles = {};
          requiredDocUploads.forEach((doc) => {
            if (doc.uploaded) {
              newDocUploadStatus[doc.documentName] = true;
              if (doc.fileName) newUploadedDocFiles[doc.documentName] = doc.fileName;
            }
          });
          setDocUploadStatus(newDocUploadStatus);
          setUploadedDocFiles(newUploadedDocFiles);
        }
      })
      .catch((err) => {
        const backendMessage = err.response?.data;
        setFeedback({
          type: 'danger',
          message: typeof backendMessage === 'string' && backendMessage
            ? backendMessage
            : 'Failed to regenerate the Scoring guide/Mark sheet. Please try again.',
        });
      })
      .finally(() => setRegeneratingDocs(false));
  };

  // Fetch upload status from backend and pre-fill UI. Polled (not just
  // fetched once) because "Scoring guide"/"Mark sheet" now get attached as a
  // background side effect of saving scores (see
  // ScoringGuideExportService.attachAutoGeneratedPdfsIfComplete on the
  // backend) - there's no client-triggered action to hang a one-off refetch
  // off of, so this page needs to notice on its own once the last candidate
  // gets scored.
  useEffect(() => {
    if (!assignmentId) return;
    const fetchUploadStatus = () => {
      axios
        .get(`/api/assignments/${assignmentId}/upload-status`)
        .then((res) => {
          const { studentUploads, requiredDocUploads, bulkCandidateWorkUploaded } = res.data || {};
          // Pre-fill student upload status and file names
          if (studentUploads) {
            const newUploadStatus = {};
            const newUploadedStudentFiles = {};
            studentUploads.forEach((stu) => {
              if (stu.uploaded) {
                newUploadStatus[stu.registrationNumber] = 'success';
                if (stu.fileName) newUploadedStudentFiles[stu.registrationNumber] = stu.fileName;
              }
            });
            setUploadStatus(newUploadStatus);
            setUploadedStudentFiles(newUploadedStudentFiles);
          }
          // Pre-fill required doc upload status and file names
          if (requiredDocUploads) {
            const newDocUploadStatus = {};
            const newUploadedDocFiles = {};
            requiredDocUploads.forEach((doc) => {
              if (doc.uploaded) {
                newDocUploadStatus[doc.documentName] = true;
                if (doc.fileName) newUploadedDocFiles[doc.documentName] = doc.fileName;
              }
            });
            setDocUploadStatus(newDocUploadStatus);
            setUploadedDocFiles(newUploadedDocFiles);
          }
          // Handle bulk candidate work upload status
          if (bulkCandidateWorkUploaded) {
            setCandidateWorkUploadStatus('success');
            setAllCandidateWorkUploaded(true);
          }
        })
        .catch((err) => {
          console.error('Error fetching upload status:', err);
        });
    };
    fetchUploadStatus();
    const interval = setInterval(fetchUploadStatus, 15000);
    return () => clearInterval(interval);
  }, [assignmentId]);

  // Renders the Status cell for one candidate: a recorded ABSENT/EMPTY
  // disposition takes priority over everything else, then actual upload
  // outcomes, then the last folder-scan result, with the corresponding
  // action button (Mark Absent / Confirm Save as Empty) offered wherever
  // relevant so the examiner never has to leave this table.
  const renderCandidateStatusCell = (student) => {
    const regNo = student.registrationNumber;
    const alreadyUploaded = uploadStatus[regNo] === 'success';
    const chosenFile = studentFiles[regNo];
    const finalized = finalizedStatus[regNo];

    if (finalized) {
      return (
        <div className="mt-2">
          <Badge bg={finalized.status === 'ABSENT' ? 'secondary' : 'warning'} text={finalized.status === 'ABSENT' ? undefined : 'dark'}>
            {finalized.status === 'ABSENT' ? 'Absent' : 'Empty (confirmed)'}
          </Badge>
          <div className="small text-muted mt-1" style={{ maxWidth: 'min(160px, 100%)', wordBreak: 'break-word' }} title={finalized.reason}>{finalized.reason}</div>
        </div>
      );
    }
    if (alreadyUploaded) {
      return <div className="mt-2"><FaCheck style={{ fontSize: '1.5rem', color: 'green' }} title="Uploaded" /></div>;
    }
    if (uploadStatus[regNo] === 'error') {
      return <div className="mt-2"><FaTimesCircle style={{ fontSize: '1.5rem', color: 'red' }} title="Upload failed" /></div>;
    }
    const matchState = folderMatchStatus[regNo];
    if (matchState === 'matched' || chosenFile) {
      return <div className="mt-2"><FaCheck style={{ fontSize: '1.5rem', color: 'green' }} title="Folder found" /></div>;
    }
    if (matchState === 'error') {
      return (
        <div className="mt-2">
          <FaExclamationTriangle
            style={{ fontSize: '1.5rem', color: '#e0a800' }}
            title={folderErrorReasons[regNo] || 'Folder found but could not be processed'}
          />
          <div className="mt-1">
            <Button size="sm" variant="outline-warning" onClick={() => handleConfirmEmpty(student)}>Confirm Empty Folder</Button>
          </div>
        </div>
      );
    }
    // matchState === 'missing', or no scan has been run yet at all. A
    // browser can't tell "no folder exists" apart from "a folder exists but
    // has zero files in it" (empty directories never appear in a
    // webkitdirectory scan), so both options are offered here and it's left
    // to the examiner's own knowledge of what they saw to pick the right one.
    return (
      <div className="mt-2">
        {regNo in folderMatchStatus && <FaTimesCircle style={{ fontSize: '1.5rem', color: 'red' }} title="No matching folder found" />}
        <div className="mt-1 d-flex flex-column gap-1 align-items-center">
          <Button size="sm" variant="outline-secondary" onClick={() => handleMarkAbsent(student)}>Mark Absent</Button>
          <Button size="sm" variant="outline-warning" onClick={() => handleConfirmEmpty(student)}>Confirm Empty Folder</Button>
        </div>
      </div>
    );
  };

  // Hard gate, not just disabled buttons: once assignmentInfo has loaded,
  // an in-progress job (ACCEPTED_BY_EXAMINER) is only reachable while the
  // upload window is actually "during" - before it opens or after it
  // closes, this replaces the whole upload/scoring page (including the
  // embedded ScoringGuideTable) with a locked message, instead of a page
  // full of disabled controls someone could still poke at via devtools.
  // WORK_UNDER_REVIEW/COMPLETED jobs stay fully viewable regardless of the
  // window - those are read-only review of work already submitted, not
  // active scoring. The backend enforces the same rule server-side on
  // every write this page can make (candidate-status, document upload,
  // candidate scores, finalize) regardless of what this page shows.
  if (
    assignmentInfo &&
    String(assignmentInfo.status).toUpperCase() === 'ACCEPTED_BY_EXAMINER' &&
    uploadPhase.phase !== 'during'
  ) {
    return (
      <Container className="mt-2">
        <TitleWithBack title="Upload Students Work" />
        <Alert
          variant={uploadPhase.phase === 'before' ? 'warning' : 'danger'}
          className="mt-4 text-center"
          style={{ fontWeight: 'bold', fontSize: '1.05rem' }}
        >
          {uploadPhase.phase === 'before' && uploadPhase.window && (
            <>The upload window for this exam has not opened yet. It opens in: {formatCountdown(uploadPhase.window.start - now)}.</>
          )}
          {uploadPhase.phase === 'after' && (
            <>The upload window for this exam has closed. Uploading and scoring are no longer available for this job.</>
          )}
          {uploadPhase.phase === 'unknown' && (
            <>This exam's schedule is not configured. Contact the administrator.</>
          )}
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-2">
      <TitleWithBack title="Upload Students Work" />

      {/* Upload window countdown */}
      {assignmentInfo && (
        <Alert
          variant={uploadPhase.phase === 'during' ? 'success' : uploadPhase.phase === 'before' ? 'warning' : 'danger'}
          className="mb-4 text-center"
          style={{ fontWeight: 'bold', fontSize: '1.05rem' }}
        >
          {uploadPhase.phase === 'before' && uploadPhase.window && (
            <>Uploading opens in: {formatCountdown(uploadPhase.window.start - now)}</>
          )}
          {uploadPhase.phase === 'during' && uploadPhase.window && (
            <>Upload window closes in: {formatCountdown(uploadPhase.window.end - now)}</>
          )}
          {uploadPhase.phase === 'after' && (
            <>Upload window has closed. Uploading is no longer possible for this exam.</>
          )}
        </Alert>
      )}

      {/* Sections below render as a manually-expandable accordion (several
          can stay open at once) for the team leader/solo examiner, who has
          enough sections to benefit from collapsing the ones they aren't
          using right now - the first section stays open by default. A
          regular team member only ever sees one or two sections, so Section
          renders those as plain always-expanded Cards instead (see Section
          above) - wrapping them in Accordion here is harmless either way,
          since Accordion has no opinion about non-Accordion.Item children. */}
      <Accordion alwaysOpen defaultActiveKey={['0']} className="mb-1">
      {/* IMPORTANT INFORMATION */}
      <Section eventKey="0" title="IMPORTANT INFORMATION" accordion={canActAsTeamLeader}>
          <ul style={{ listStyleType: 'decimal', marginLeft: '1.2rem' }}>
            {isTeamAssignment && (
              canActAsTeamLeader ? (
                <li>
                  You're the <strong>team leader</strong> for this job: you can search for and score candidates
                  from the shared roster like any team member, and you're also the only one who can write the
                  report, upload any other required documents, and mark the job complete once the whole team's
                  scoring is done.
                </li>
              ) : (
                <li>
                  You're a <strong>team member</strong> on this job: your part is to search for and score
                  candidates from the shared roster.
                  {assignmentInfo?.teamLeader?.fullName
                    ? ` ${assignmentInfo.teamLeader.fullName} is the team leader and handles the report, any other required documents, and marking the job complete.`
                    : ' The team leader handles the report, any other required documents, and marking the job complete.'}
                </li>
              )
            )}
            <li>
              Uploading is only possible during the exam's upload window (shown above as a countdown) - it
              activates automatically at the exam's start time and locks again once the window closes.
            </li>
            {showCandidatesWork && (
              <>
                <li>
                  For candidate work: either upload each student's work individually as a single file named
                  with their registration number and full name, or use the bulk folder upload - select one
                  parent folder containing one subfolder per candidate (also named with their registration
                  number and full name) and each candidate's folder will be matched against the roster and
                  compressed automatically.
                </li>
                <li>
                  Either way, nothing is saved until every candidate shows a green tick and you click "Upload
                  All Candidate Work". A red cross means that candidate's file/folder was not found or not
                  yet uploaded. An amber warning icon means a folder was found for that candidate but couldn't
                  be used (e.g. it was empty) - hover it to see why, fix that candidate's folder, and re-select
                  the parent folder to re-scan; it will not stop the other candidates from being processed.
                </li>
                <li>
                  If a candidate has no matching folder, either use "Mark Absent" (no folder exists - nothing is
                  uploaded), or "Confirm Empty Folder" if you know a folder does exist but is genuinely empty
                  (a browser can't detect an empty folder during the scan, so this is based on your own
                  knowledge) - the empty folder is still uploaded as their submission. Both require a short
                  reason for accountability. If a folder exists but doesn't match anyone on the roster, it's a
                  candidate not on the roster - shown separately below the table with a provision to add them
                  and upload their folder, recorded as having sat under protest; if that candidate's folder is
                  itself empty, use "Add candidate under protest (no folder / empty folder)" instead, which asks
                  for the same confirmation and reason.
                </li>
              </>
            )}
            {showScoringGuideTable && (
              <>
                <li>
                  For the Scoring Guide: search for a candidate by registration number or name and select them
                  to start scoring against each assessment criterion under the Process (P) and Product (R)
                  columns. A candidate is open to any team member until someone enters their first score - from
                  then on it shows "In Progress" with that examiner's name, and only they can continue scoring
                  that candidate until it's finalized. Scores save automatically a couple of seconds after you
                  stop typing - partial scores are fine and never lock the table, so come back anytime within
                  the upload window.
                </li>
                <li>
                  Once you've scored the required number of questions for a candidate, a "Finalize & Lock
                  Scores" button appears - check every score carefully first, since finalizing is permanent and
                  locks the candidate for everyone, including you.
                </li>
                <li>
                  "Mark Absent" is only available before a candidate has any score recorded - it disappears
                  once scoring has started for them, by you or anyone else.
                </li>
                {isTeamAssignment && (
                  <li>
                    "My Scored Candidates" is your own read-only record of everyone you've personally scored,
                    for reference.
                  </li>
                )}
                {canActAsTeamLeader && (
                  <li>
                    As team leader, you can also see the full Team Report (every candidate and who scored
                    them), and preview the Final Marksheet and General Scoring Guide once the whole roster is
                    fully scored or marked absent - at that point the Scoring Guide and Mark Sheet requirements
                    below are satisfied automatically, with PDF versions generated and attached for you.
                  </li>
                )}
              </>
            )}
            {showReportEditor && canActAsTeamLeader && (
              <li>
                For the Assessment Report: type and format it directly in the Assessment Report section below
                (bold, italic, underline, and lists) - clicking "Save Report" turns it into a PDF matching the
                official template and attaches it as the Report document. It cannot be edited after saving.
              </li>
            )}
            {plainOtherRequiredDocs.length > 0 && canActAsTeamLeader && (
              <li>
                Upload the remaining required documents ({plainOtherRequiredDocs.join(', ')}) as PDF files in
                the section below.
              </li>
            )}
            {canActAsTeamLeader && (
              <li>
                "Mark Uploading as Complete" only becomes available once every requirement above is satisfied.
              </li>
            )}
            <li>Ensure you have a stable internet connection for this to be successful.</li>
          </ul>
      </Section>

      {/* Feedback Message */}
      {feedback && (
        <Alert
          variant={feedback.type === 'danger' ? 'danger' : 'success'}
          className="mb-4"
          style={{
            position: 'fixed',
            top: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            width: 'min(400px, calc(100vw - 2rem))',
            textAlign: 'center',
            boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
          }}
        >
          {feedback.message}
        </Alert>
      )}


      {/* Fallback warning if no required documents */}
      {requiredDocs.length === 0 && (
        <Alert variant="warning">
          Nothing to show here
        </Alert>
      )}

      {/* Candidate Work Upload Section */}
      {showCandidatesWork && (
        <Section eventKey="1" title="UPLOAD INDIVIDUAL STUDENT WORK" accordion={canActAsTeamLeader}>

            <div className="mb-3 p-3" style={{ backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '6px' }}>
              <div className="fw-bold mb-2">Bulk folder upload</div>
              <div className="small text-muted mb-2">
                Select the parent folder containing one subfolder per candidate (each subfolder named with the
                candidate's name and registration number). Every candidate folder will be matched against the roster
                and compressed into its own zip automatically - nothing is saved until you review the results below
                and click "Upload All Candidate Work".
              </div>
              <input
                type="file"
                className="form-control"
                webkitdirectory=""
                directory=""
                multiple
                onChange={handleFolderSelect}
                disabled={!uploadWindowOpen || folderScanning}
              />
              {folderScanning && (
                <div className="mt-2 small text-muted">Scanning folders and matching candidates...</div>
              )}
              {folderScanSummary && (
                <Alert
                  variant={
                    folderScanSummary.missingStudents.length === 0 && folderScanSummary.erroredStudents.length === 0
                      ? 'success'
                      : 'warning'
                  }
                  className="mt-3 mb-0"
                >
                  {folderScanSummary.missingStudents.length === 0 && folderScanSummary.erroredStudents.length === 0 ? (
                    <>All {students.length} candidate folders were matched.</>
                  ) : (
                    <>
                      {folderScanSummary.matchedCount} of {students.length} candidate folders matched.
                      {folderScanSummary.missingStudents.length > 0 && (
                        <div className="mt-1">
                          <strong>Missing:</strong> {folderScanSummary.missingStudents.map(s => `${s.name} (${s.registrationNumber})`).join(', ')}.
                        </div>
                      )}
                      {folderScanSummary.erroredStudents.length > 0 && (
                        <div className="mt-1">
                          <strong>Needs attention:</strong> {folderScanSummary.erroredStudents.map(s => `${s.name} (${s.registrationNumber}) - ${folderErrorReasons[s.registrationNumber] || 'could not be processed'}`).join('; ')}.
                        </div>
                      )}
                      <div className="mt-1">
                        Rectify the folder(s) above and re-select the corrected parent folder to redo the whole
                        batch, or use the "Select File" column below to upload just those candidates individually.
                        The upload button stays disabled until every candidate is accounted for.
                      </div>
                    </>
                  )}
                </Alert>
              )}
              {unmatchedFolders.length > 0 && (
                <Alert variant="info" className="mt-3 mb-0">
                  <strong>{unmatchedFolders.length} folder(s) don't match anyone on the roster:</strong>
                  <ul className="mb-0 mt-2 ps-3">
                    {unmatchedFolders.map((f) => (
                      <li key={f.folderName} className="mb-2">
                        {f.folderName} ({f.fileCount} file{f.fileCount === 1 ? '' : 's'})
                        {protestFormOpenFor === f.folderName ? (
                          <div className="mt-2 p-2 border rounded bg-white" style={{ maxWidth: 'min(420px, 100%)' }}>
                            <div className="small text-muted mb-2">
                              If this is a genuine candidate not on the roster, add them and upload their folder now.
                              They will be recorded as having sat under protest.
                            </div>
                            <Form.Group className="mb-2">
                              <Form.Label className="small mb-0">Registration Number</Form.Label>
                              <Form.Control
                                size="sm"
                                placeholder="e.g. UVT660/U/26/M/NDICT/0016"
                                value={protestForm.registrationNumber}
                                onChange={(e) => setProtestForm((prev) => ({ ...prev, registrationNumber: e.target.value }))}
                              />
                              <div className="small text-muted mt-1">
                                Use "/" between segments to match the rest of the roster - a hyphenated value (as often seen in folder names) will be auto-converted.
                              </div>
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Label className="small mb-0">Full Name</Form.Label>
                              <Form.Control
                                size="sm"
                                value={protestForm.name}
                                onChange={(e) => setProtestForm((prev) => ({ ...prev, name: e.target.value }))}
                              />
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Label className="small mb-0">Email</Form.Label>
                              <Form.Control
                                size="sm"
                                type="email"
                                value={protestForm.email}
                                onChange={(e) => setProtestForm((prev) => ({ ...prev, email: e.target.value }))}
                              />
                            </Form.Group>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant="warning"
                                disabled={protestSubmitting}
                                onClick={() => handleAddProtestCandidate(f.folderName)}
                              >
                                {protestSubmitting ? 'Adding...' : 'Add & Upload (Sat Under Protest)'}
                              </Button>
                              <Button size="sm" variant="outline-secondary" onClick={() => setProtestFormOpenFor(null)}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline-warning"
                            className="ms-2"
                            onClick={() => {
                              setProtestFormOpenFor(f.folderName);
                              setProtestForm({ registrationNumber: '', name: '', email: '' });
                            }}
                          >
                            Add to roster (sat under protest)
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}

              <div className="mt-3 p-2 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="small text-muted mb-2">
                  Know of a candidate who isn't on the roster and has no folder (or an empty one)? A browser can't
                  detect an empty folder during the scan, so add them here directly instead.
                </div>
                {protestFormOpenFor === MANUAL_EMPTY_PROTEST ? (
                  <div className="p-2 border rounded bg-white" style={{ maxWidth: 'min(420px, 100%)' }}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small mb-0">Registration Number</Form.Label>
                      <Form.Control
                        size="sm"
                        placeholder="e.g. UVT660/U/26/M/NDICT/0016"
                        value={protestForm.registrationNumber}
                        onChange={(e) => setProtestForm((prev) => ({ ...prev, registrationNumber: e.target.value }))}
                      />
                      <div className="small text-muted mt-1">
                        Use "/" between segments to match the rest of the roster.
                      </div>
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label className="small mb-0">Full Name</Form.Label>
                      <Form.Control
                        size="sm"
                        value={protestForm.name}
                        onChange={(e) => setProtestForm((prev) => ({ ...prev, name: e.target.value }))}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label className="small mb-0">Email</Form.Label>
                      <Form.Control
                        size="sm"
                        type="email"
                        value={protestForm.email}
                        onChange={(e) => setProtestForm((prev) => ({ ...prev, email: e.target.value }))}
                      />
                    </Form.Group>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="warning"
                        disabled={protestSubmitting}
                        onClick={() => handleAddProtestCandidate(MANUAL_EMPTY_PROTEST)}
                      >
                        {protestSubmitting ? 'Adding...' : 'Confirm Empty & Add (Sat Under Protest)'}
                      </Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => setProtestFormOpenFor(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline-warning"
                    onClick={() => {
                      setProtestFormOpenFor(MANUAL_EMPTY_PROTEST);
                      setProtestForm({ registrationNumber: '', name: '', email: '' });
                    }}
                  >
                    Add candidate under protest (no folder / empty folder)
                  </Button>
                )}
              </div>
            </div>

            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>SN</th>
                  <th>Student Registration no</th>
                  <th>Student Name</th>
                  <th>Select File</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {students.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center">
                      Nothing to show here
                    </td>
                  </tr>
                ) : (
                  students.map((student, index) => {
                    const regNo = student.registrationNumber;
                    const alreadyUploaded = uploadStatus[regNo] === 'success';
                    const chosenFile = studentFiles[regNo];
                    const uploadedFileName = uploadedStudentFiles[regNo];
                    return (
                      <tr key={regNo}>
                        <td>{index + 1}</td>
                        <td>{student.registrationNumber}</td>
                        <td>{student.name}</td>
                        <td>
                          <div className="d-flex flex-wrap align-items-center gap-2">
                          <Form.Control
                            type="file"
                            accept=".zip,.rar,.7z"
                            style={{ minWidth: '180px' }}
                            onChange={(e) =>
                              handleStudentFileChange(regNo, e.target.files[0])
                            }
                             disabled={alreadyUploaded || !uploadWindowOpen}
                          />
                          <Button
                           variant="primary"
                           size="sm"
                           disabled={!studentFiles[regNo] || alreadyUploaded || !uploadWindowOpen}
                           onClick= {() => handleUploadSingleCandidateWork(regNo)}
                               >
                                Upload
                          </Button>
                          </div>
                          {/* Show file name: chosen file, else uploaded file, else nothing */}
                          <div className="mt-1 small">
                            {chosenFile ? (
                              <span className="text-primary">
                                {chosenFile.name}
                                {fileSource[regNo] === 'folder' && ' (matched from bulk folder)'}
                              </span>
                            ) : uploadedFileName ? (
                              <span className="text-success">{uploadedFileName}</span>
                            ) : (
                              <span className="text-muted">No file chosen</span>
                            )}
                          </div>
                          {progress[student.registrationNumber] > 0 && progress[student.registrationNumber] < 100 && (
                          <ProgressBar
                          label={`${progress[student.registrationNumber]}%`}
                          now={progress[student.registrationNumber]}
                          className="mt-2"
                          striped
                          animated
                          />   
                        )}
                        {alreadyUploaded && (
                          <div className="mt-1 text-success small">Already uploaded</div>
                        )}
                        </td>
                        <td className="text-center">
                          {renderCandidateStatusCell(student)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
            <Button
              variant="primary"
              size="sm"
              disabled={!allStudentsReadyForBulk || candidateWorkUploadStatus === 'success' || allCandidateWorkUploaded || !uploadWindowOpen}
              onClick={handleUploadAllCandidateWork}
            >
              Upload All Candidate Work
            </Button>
            {candidateWorkUploadStatus === 'success' && (
              <div className="mt-2">
                <FaCheck style={{ fontSize: '1.5rem', color: 'green' }} />
              </div>
            )}
           {progress.allCandidates > 0 && (
           <ProgressBar
           now={progress.allCandidates}
           label={`${progress.allCandidates}%`}
           className="mt-3"
           striped
           animated
           style={{ height: '8px', width: '100%' }}
          />
          )}
        </Section>
      )}

      {/* Scoring Guide Section (RLP/OSM modules only) */}
      {showScoringGuideTable && (
        <Section
          eventKey="2"
          title={(
            <>
              SCORING GUIDE
              {scoringGuideComplete && (
                <FaCheck style={{ fontSize: '1.2rem', color: 'green', marginLeft: 8 }} />
              )}
            </>
          )}
          accordion={canActAsTeamLeader}
        >
            <ScoringGuideTable
              moduleId={moduleDetails?.id}
              assignmentId={assignmentId}
              examCenterId={examCenterId}
              assignmentInfo={assignmentInfo}
              moduleDetails={moduleDetails}
              uploadWindowOpen={uploadWindowOpen}
              onCompletionChange={setScoringGuideComplete}
            />
        </Section>
      )}

      {/* Scoring guide/Mark sheet PDFs are generated and attached
          automatically once every candidate on the roster is fully scored -
          nothing to prepare or upload here, just a status indicator. Team
          leader (or solo examiner) only - a regular team member's own job is
          done once their candidates are scored, so this status view (and the
          retry action below) isn't relevant to them. */}
      {(showScoringGuideTable || showAutoGeneratedMarksheetStatus) && canActAsTeamLeader && (
        <Section eventKey="3" title="AUTO-GENERATED DOCUMENTS" accordion={canActAsTeamLeader}>
            <p className="text-muted small mb-3">
              The Scoring Guide and Mark Sheet are generated automatically as PDFs, from the same
              scores entered above, once every candidate is fully scored - there's nothing to attach here yourself.
            </p>
            {showScoringGuideTable && (
              <div className="d-flex align-items-center gap-2 mb-2">
                <strong style={{ minWidth: 160 }}>Scoring guide</strong>
                {docUploadStatus['Scoring guide'] ? (
                  <>
                    <span className="text-success">
                      <FaCheck style={{ marginRight: 6 }} />Generated and attached
                    </span>
                    <Button variant="link" size="sm" className="p-0" onClick={() => setDocPreviewKind('guide')}>
                      Preview
                    </Button>
                  </>
                ) : (
                  <span className="text-muted">
                    {scoringGuideComplete ? 'Pending - not yet generated' : "Pending - complete all candidates' scores first"}
                  </span>
                )}
              </div>
            )}
            {showAutoGeneratedMarksheetStatus && (
              <div className="d-flex align-items-center gap-2">
                <strong style={{ minWidth: 160 }}>Mark sheet</strong>
                {docUploadStatus['Mark sheet'] ? (
                  <>
                    <span className="text-success">
                      <FaCheck style={{ marginRight: 6 }} />Generated and attached
                    </span>
                    <Button variant="link" size="sm" className="p-0" onClick={() => setDocPreviewKind('marksheet')}>
                      Preview
                    </Button>
                  </>
                ) : (
                  <span className="text-muted">
                    {scoringGuideComplete ? 'Pending - not yet generated' : "Pending - complete all candidates' scores first"}
                  </span>
                )}
              </div>
            )}
            {/* Every candidate is fully scored (scoringGuideComplete) but at
                least one of the two PDFs above still isn't attached - the
                one-shot auto-generation must have failed silently earlier.
                Only the team leader/solo examiner can retry, matching who
                can act on the rest of this page. */}
            {scoringGuideComplete && canActAsTeamLeader &&
              ((showScoringGuideTable && !docUploadStatus['Scoring guide']) ||
                (showAutoGeneratedMarksheetStatus && !docUploadStatus['Mark sheet'])) && (
                <div className="mt-2">
                  <Button variant="outline-warning" size="sm" onClick={handleRegenerateAutoDocuments} disabled={regeneratingDocs}>
                    {regeneratingDocs ? 'Regenerating...' : 'Retry generation'}
                  </Button>
                  <span className="text-muted small ms-2">
                    Every candidate is scored, but generation didn't complete yet - this usually clears up in one click.
                  </span>
                </div>
              )}
        </Section>
      )}
      <PdfPreviewModal
        show={!!docPreviewKind}
        onClose={() => setDocPreviewKind(null)}
        title={docPreviewKind === 'marksheet' ? 'Final Marksheet' : 'General Scoring Guide'}
        fetchUrl={
          docPreviewKind === 'marksheet'
            ? `/api/scoring-criteria/assignment/${assignmentId}/marksheet/preview`
            : docPreviewKind === 'guide'
            ? `/api/scoring-criteria/assignment/${assignmentId}/general-guide/preview`
            : null
        }
      />

      {/* Assessment Report Section (all exam types) - team leader only for a
          multi-examiner team, since the report/attendance are examiner-team
          level artifacts, not per-candidate work. A non-leader team member
          gets nothing here at all - not even a placeholder message, per
          explicit instruction - they simply score their candidates and
          merge with the team leader. */}
      {showReportEditor && canActAsTeamLeader && (
        <Section
          eventKey="4"
          title={(
            <>
              ASSESSMENT REPORT
              {docUploadStatus['Report'] && (
                <FaCheck style={{ fontSize: '1.2rem', color: 'green', marginLeft: 8 }} />
              )}
            </>
          )}
          accordion={canActAsTeamLeader}
        >
            <ReportEditor
              assignmentId={assignmentId}
              examCentreId={examCenterId}
              alreadySaved={!!docUploadStatus['Report']}
              uploadWindowOpen={uploadWindowOpen}
              onSaved={() => {
                setDocUploadStatus((prev) => ({ ...prev, Report: true }));
                setFeedback({ type: 'success', message: 'Report saved and attached successfully.' });
              }}
            />
        </Section>
      )}

      {/* Other Required Documents Upload Section - team leader only for a
          multi-examiner team. Nothing rendered at all for a non-leader. */}
      {plainOtherRequiredDocs.length > 0 && canActAsTeamLeader && (
        <Section eventKey="5" title="UPLOAD REQUIRED DOCUMENTS" accordion={canActAsTeamLeader}>
            {plainOtherRequiredDocs.map((docName) => {
              const file = otherDocsFiles[docName];
              const uploadedFileName = uploadedDocFiles[docName];
              const fileChosenName = file ? file.name : (uploadedFileName ? uploadedFileName : 'No file chosen');
              const alreadyUploaded = docUploadStatus[docName];
              return (
                <div key={docName} className="d-flex flex-wrap align-items-center gap-2 mb-3 p-2 border rounded bg-white">
                  <strong style={{ flex: '1 1 140px' }}>{docName}</strong>
                  <Form.Control
                    type="file"
                    accept=".pdf"
                    style={{ flex: '2 1 220px', minWidth: 0 }}
                    onChange={(e) => handleOtherDocFileChange(docName, e.target.files[0])}
                    disabled={alreadyUploaded || !uploadWindowOpen}
                  />
                  <span
                    className={alreadyUploaded ? 'text-success' : 'text-muted'}
                    style={{ flex: '1 1 140px', wordBreak: 'break-word' }}
                  >
                    {fileChosenName}
                    {alreadyUploaded && <FaCheck style={{ fontSize: '1.2rem', color: 'green', marginLeft: 8 }} />}
                  </span>
                  <div style={{ flex: '0 0 auto' }}>
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={!file || alreadyUploaded || !uploadWindowOpen}
                      onClick={() => handleUploadDocument(docName)}
                    >
                      Upload
                    </Button>
                    {progress[docName] > 0 && progress[docName] < 100 && (
                      <ProgressBar
                      now={progress[docName]}
                      className="mt-2"
                      label={`${progress[docName]}%`}
                      striped
                      animated
                      style={{ minWidth: '120px' }}
                      />
                      )}
                  </div>
                </div>
              );
            })}
        </Section>
      )}
      </Accordion>

      {/* Mark Uploading as Complete Section - team leader (or solo examiner)
          only. Nothing rendered at all for a non-leader - their job is done
          once they've scored their candidates and merged their list back
          (guidance for that lives in the Team Merge Status panel above,
          inside the Scoring Guide section). Deliberately kept OUT of the
          accordion above (always a plain, always-visible Card) - this is the
          final, most consequential action on the page and shouldn't be
          something that can get collapsed out of view. */}
      {canActAsTeamLeader && (
        <Section eventKey="6" title="FINALIZE UPLOAD" accordion={false}>
            <Button
              variant="success"
              onClick={handleMarkUploadingComplete}
              disabled={!canMarkComplete || !uploadWindowOpen}
            >
              Mark Uploading as Complete
            </Button>
        </Section>
      )}
    </Container>
  );
}
